import { PaginationState, SortingState } from '@tanstack/react-table';

/**
 * A sort id mapped onto the PostgREST expression that can order by it.
 *
 * A grid column id is not automatically a database column: it can be a UI-only
 * column (`actions`), a PostgREST embed alias (`updated_by`), or a key inside a
 * jsonb column (`job_card_started_at`). Handing one of those to `.order()` makes
 * Postgres raise 42703 and fails the whole request — ARTIC-HUB-16T and -16C.
 */
export type SortableColumns = Readonly<Record<string, string>>;

export default class SupabaseQueryHelper {
  static addPagination = (query: any, pagination: PaginationState) =>
    query.range(
      pagination.pageIndex * pagination.pageSize,
      (pagination.pageIndex + 1) * pagination.pageSize - 1,
    );

  /**
   * With `sortableColumns`, a sort is ordered by its mapped expression and any id
   * outside the map is dropped before it reaches the wire. Without it, the sort id
   * is used verbatim — the caller is then asserting every id it can produce is a
   * real column.
   */
  static addSorting = (query: any, sorting: SortingState, sortableColumns?: SortableColumns) =>
    sorting.forEach((sort) => {
      const expression = sortableColumns ? sortableColumns[sort.id] : sort.id;
      // Undefined for an id the map does not carry, and a function or an object
      // for one inherited from Object.prototype (`constructor`, `__proto__`).
      if (typeof expression !== 'string') return;

      const columns = expression.split('.');
      const desc = sort.desc;
      const column = columns[columns.length - 1];
      if (columns.length > 1) {
        query.order(column, {
          ascending: !desc,
          foreignTable: columns.slice(0, columns.length - 1).join('.'),
          nullsFirst: false,
        });
      } else {
        query.order(columns[0], {
          ascending: !desc,
          nullsFirst: false,
        });
      }
    });

  static getPageCount = (count: number | null, pagination?: PaginationState) =>
    count && pagination ? Math.ceil(count / pagination.pageSize) : 0;
}
